/**
 * WorkflowIconPicker — curated icon set + picker grid for workflows.
 *
 * Usage:
 *   import { WorkflowIcon, WorkflowIconPicker, WORKFLOW_ICONS } from './WorkflowIconPicker';
 *
 *   // Render a single icon by its key
 *   <WorkflowIcon iconKey="RiToolsLine" size={16} color="currentColor" />
 *
 *   // Render the picker grid
 *   <WorkflowIconPicker value={selectedKey} onChange={setSelectedKey} />
 */
import React from 'react';
import {
  RiToolsLine,
  RiRobot2Line,
  RiRouteLine,
  RiMicroscopeLine,
  RiBrainLine,
  RiSearchLine,
  RiBarChart2Line,
  RiDatabase2Line,
  RiCodeLine,
  RiFileTextLine,
  RiMailLine,
  RiCalendarLine,
  RiShoppingCartLine,
  RiMoneyDollarCircleLine,
  RiUserLine,
  RiTeamLine,
  RiGlobalLine,
  RiLightbulbLine,
  RiCpuLine,
  RiBookOpenLine,
  RiChat3Line,
  RiMagicLine,
  RiFlowChart,
  RiSparklingLine,
} from 'react-icons/ri';

// ---------------------------------------------------------------------------
// Curated icon map
// ---------------------------------------------------------------------------

/** Ordered list of available workflow icons.  The key is stored in the DB. */
export const WORKFLOW_ICON_LIST = [
  { key: 'RiFlowChart',              label: 'Workflow',    Component: RiFlowChart },
  { key: 'RiToolsLine',              label: 'Tools',       Component: RiToolsLine },
  { key: 'RiRobot2Line',             label: 'Robot',       Component: RiRobot2Line },
  { key: 'RiRouteLine',              label: 'Route',       Component: RiRouteLine },
  { key: 'RiMicroscopeLine',         label: 'Research',    Component: RiMicroscopeLine },
  { key: 'RiBrainLine',              label: 'Brain',       Component: RiBrainLine },
  { key: 'RiSearchLine',             label: 'Search',      Component: RiSearchLine },
  { key: 'RiBarChart2Line',          label: 'Chart',       Component: RiBarChart2Line },
  { key: 'RiDatabase2Line',          label: 'Database',    Component: RiDatabase2Line },
  { key: 'RiCodeLine',               label: 'Code',        Component: RiCodeLine },
  { key: 'RiFileTextLine',           label: 'Document',    Component: RiFileTextLine },
  { key: 'RiMailLine',               label: 'Email',       Component: RiMailLine },
  { key: 'RiCalendarLine',           label: 'Calendar',    Component: RiCalendarLine },
  { key: 'RiShoppingCartLine',       label: 'Commerce',    Component: RiShoppingCartLine },
  { key: 'RiMoneyDollarCircleLine',  label: 'Finance',     Component: RiMoneyDollarCircleLine },
  { key: 'RiUserLine',               label: 'User',        Component: RiUserLine },
  { key: 'RiTeamLine',               label: 'Team',        Component: RiTeamLine },
  { key: 'RiGlobalLine',             label: 'Global',      Component: RiGlobalLine },
  { key: 'RiLightbulbLine',          label: 'Idea',        Component: RiLightbulbLine },
  { key: 'RiCpuLine',                label: 'CPU',         Component: RiCpuLine },
  { key: 'RiBookOpenLine',           label: 'Knowledge',   Component: RiBookOpenLine },
  { key: 'RiChat3Line',              label: 'Chat',        Component: RiChat3Line },
  { key: 'RiMagicLine',              label: 'Magic',       Component: RiMagicLine },
  { key: 'RiSparklingLine',          label: 'Spark',       Component: RiSparklingLine },
];

/** Object keyed by icon key for O(1) lookup. */
export const WORKFLOW_ICONS = Object.fromEntries(
  WORKFLOW_ICON_LIST.map(({ key, Component }) => [key, Component])
);

// ---------------------------------------------------------------------------
// WorkflowIcon — renders one icon by key
// ---------------------------------------------------------------------------

/**
 * Render a workflow icon by its string key.  Falls back to RiFlowChart
 * when the key is not recognised or is null/undefined.
 *
 * @param {string|null} iconKey - Key from WORKFLOW_ICONS, e.g. "RiToolsLine"
 * @param {number} [size=16]    - Icon size in pixels
 * @param {string} [color]      - CSS colour string
 * @param {object} [style]      - Additional inline styles
 */
export function WorkflowIcon({ iconKey, size = 16, color, style }) {
  const Icon = (iconKey && WORKFLOW_ICONS[iconKey]) || RiFlowChart;
  return (
    <Icon
      size={size}
      style={{ color, flexShrink: 0, ...style }}
    />
  );
}

// ---------------------------------------------------------------------------
// WorkflowIconPicker — grid of icon buttons
// ---------------------------------------------------------------------------

/**
 * A 6-column grid of curated icon buttons.  The selected icon is highlighted.
 * Icons in `usedIcons` are shown greyed-out and cannot be selected.
 *
 * @param {string|null}    value     - Currently selected icon key
 * @param {Function}       onChange  - Called with the new icon key on selection
 * @param {Array<string>}  usedIcons - Icon keys already in use (disabled in picker)
 */
export function WorkflowIconPicker({ value, onChange, usedIcons = [] }) {
  const usedSet = React.useMemo(() => new Set(usedIcons), [usedIcons]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 4,
        padding: '6px 0',
      }}
    >
      {WORKFLOW_ICON_LIST.map(({ key, label, Component }) => {
        const isSelected = key === value;
        const isUsed = usedSet.has(key) && !isSelected;
        return (
          <button
            key={key}
            type="button"
            title={isUsed ? `${label} (already used)` : label}
            onClick={() => !isUsed && onChange(key)}
            disabled={isUsed}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 4,
              border: isSelected
                ? '2px solid var(--primary-color, #636cff)'
                : '1px solid var(--border-color, #ccc)',
              background: isSelected
                ? 'var(--primary-color, #636cff)'
                : 'transparent',
              color: isSelected
                ? '#fff'
                : isUsed
                  ? 'var(--border-color, #ccc)'
                  : 'var(--text-color)',
              cursor: isUsed ? 'not-allowed' : 'pointer',
              padding: 0,
              opacity: isUsed ? 0.35 : 1,
              transition: 'all 0.1s ease',
            }}
          >
            <Component size={16} />
          </button>
        );
      })}
    </div>
  );
}
